import { useCallback, useState } from 'react';

/**
 * Open-state machine for the user-menu categories (#1299, v4 #1377, unified
 * in #1388).
 *
 * One category open at a time, click-driven, expanding IN PLACE as an inline
 * accordion on every viewport - the side-panel presentation is retired (it
 * ran off-screen on phones). Keyboard: Enter/Space toggles via the native
 * click; expanded rows join the parent MenuList's arrow navigation, and
 * Escape bubbles to the menu (first Escape closes the open section, second
 * closes the menu - wired in UserHeader).
 *
 * The hook name survives from the hover-panels era so call sites and history
 * stay greppable.
 */
export default function useHoverPanels() {
  const [openPanel, setOpenPanel] = useState(null);

  const open = useCallback((cat) => setOpenPanel(cat), []);
  const closeAll = useCallback(() => setOpenPanel(null), []);
  const toggle = useCallback((cat) => {
    setOpenPanel((prev) => (prev === cat ? null : cat));
  }, []);

  const panelId = (cat) => `user-menu-panel-${cat}`;

  const getCategoryRowProps = useCallback((cat) => ({
    'aria-expanded': openPanel === cat,
    'aria-controls': openPanel === cat ? panelId(cat) : undefined,
    onClick: () => toggle(cat),
  }), [openPanel, toggle]);

  return {
    openPanel,
    open,
    toggle,
    closeAll,
    getCategoryRowProps,
  };
}
